import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Button, Screen, Text, TextInput } from '~/atomic';
import modalStatusService from '~/services/modalStatus';
import { useAppDispatch, useAppSelector } from '~/store';
import { selectContractById } from '~/store/contracts/selectors';
import { fetchContractDetails, updateContractFields } from '~/store/contracts/thunks';

interface FormData {
  contractNumber: string;
  guarantee: string;
  cropDescription: string;
}

export default function ContractEditView() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const contract = useAppSelector(selectContractById(id ?? ''));

  const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<FormData>({
    defaultValues: {
      contractNumber: '',
      guarantee: '',
      cropDescription: '',
    },
  });

  useEffect(() => {
    if (id) dispatch(fetchContractDetails(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (contract) {
      reset({
        contractNumber: contract.contractNumber,
        guarantee: contract.guarantee ?? '',
        cropDescription: contract.cropDescription ?? '',
      });
    }
  }, [contract, reset]);

  const onSubmit = async (data: FormData) => {
    if (!id) return;
    const ok = await dispatch(
      updateContractFields({
        id,
        body: {
          contractNumber: data.contractNumber,
          guarantee: data.guarantee || null,
          cropDescription: data.cropDescription || null,
        },
      }),
    ).unwrap();
    modalStatusService.show({
      type: ok ? 'success' : 'error',
      title: ok ? 'Contrato atualizado' : 'Não foi possível salvar',
      textFirstButton: 'Ok',
      showFirstButton: true,
      onClickFirstButton: () => {
        modalStatusService.hide();
        if (ok) navigate(-1);
      },
      handleCloseModal: modalStatusService.hide,
    });
  };

  return (
    <Screen headerTitle="Editar contrato">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-medium py-medium">
        <Box className="rounded-small bg-info-01 border border-info-02 p-small">
          <Text className="text-bodySM color-info-06">
            Apenas os campos descritivos podem ser editados. Para alterar valores ou cronograma, exclua e recadastre o contrato.
          </Text>
        </Box>
        <TextInput control={control} name="contractNumber" label="Número do contrato" icon="icon-document" />
        <TextInput control={control} name="guarantee" label="Garantia" icon="icon-security" multiline rows={3} />
        <TextInput control={control} name="cropDescription" label="Cultura/Produto" icon="icon-grid" />
        <Button type="submit" fullWidth text="Salvar alterações" loading={isSubmitting} />
      </form>
    </Screen>
  );
}
